import {
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
} from "@chakra-ui/react";
import { dataAttr } from "@chakra-ui/utils";
import { AppEnvironmentBadge } from "app/AppEnvironmentBadge";
import React from "react";
import { RouteLink } from "shared/components/link/RouteLink";
import {
  NavigationItem,
  useNavigationItemState,
} from "shared/components/navbar/NavigationItem";
import { ChevronDownIcon } from "shared/icons/ChevronDownIcon";
import { SettingsIcon } from "shared/icons/SettingsIcon";
import Logo from "shared/img/logo.svg?react";
import OperationsLogo from "shared/img/operationsLogo.svg?react";
import { rootRoute } from "shared/navigation/Routes";
import { activeFontWeightWithConsistentWidth } from "shared/utils/StyleUtils";
import zIndices from "theme/zIndices";

interface NavItemProps {
  item: NavigationItem;
}

const HeaderNavItem: React.FC<NavItemProps> = ({ item }) => {
  const itemState = useNavigationItemState(item);
  return (
    <HStack spacing="8px" paddingY="6px" paddingLeft="12px" paddingRight="8px">
      <RouteLink
        to={item.href}
        prefetch={true}
        fontSize="md"
        display="block"
        alignItems="center"
        textDecoration="none"
        userSelect="none"
        transitionProperty="common"
        transitionDuration="normal"
        px="6px"
        py="2px"
        borderRadius="4px"
        _hover={{
          bg: "primary.50",
        }}
        title={item.name}
        data-active={dataAttr(itemState === "active")}
        sx={activeFontWeightWithConsistentWidth(
          "title",
          "&[data-active], &:hover",
          {
            textColor: "gray.500",
            fontWeight: "normal",
          },
          {
            textColor: "primary.700",
            fontWeight: "medium",
          },
        )}
      >
        {item.component ?? item.name}
      </RouteLink>
      {item.items?.length && (
        <Menu offset={[-60, 8]} isLazy={true} lazyBehavior="unmount">
          <MenuButton
            color={itemState === "child-active" ? "primary.500" : "gray.400"}
            borderRadius={2}
            transitionProperty="common"
            transitionDuration="normal"
            _active={{
              bg: "primary.50",
              color: "primary.400",
            }}
            _hover={{
              bg: "primary.50",
              color: "primary.400",
            }}
          >
            <ChevronDownIcon width="20px" height="20px" />
          </MenuButton>
          <Portal>
            <MenuList>
              {item.items.map((childItem) => (
                <HeaderNavMenuItem key={childItem.id} item={childItem} />
              ))}
            </MenuList>
          </Portal>
        </Menu>
      )}
    </HStack>
  );
};

const HeaderNavMenuItem: React.FC<{
  item: NavigationItem;
}> = ({ item }) => {
  const itemState = useNavigationItemState(item);
  return (
    <MenuItem
      key={item.id}
      as={RouteLink}
      to={item.href}
      prefetch={true}
      fontWeight={itemState === "active" ? "medium" : "normal"}
      color={itemState === "active" ? "primary.500" : undefined}
    >
      {item.name}
    </MenuItem>
  );
};

export interface HeaderNavProps {
  primaryNavItems: NavigationItem[];
  userNavigationItems: NavigationItem[];
  isInternalAdminDashboardView?: boolean;
}

export const HeaderNav: React.FC<React.PropsWithChildren<HeaderNavProps>> = ({
  primaryNavItems,
  userNavigationItems,
  children,
  isInternalAdminDashboardView,
}) => (
  <Flex
    flex={1}
    alignItems="center"
    minWidth="0"
    flexWrap="wrap"
    columnGap="16px"
    py="11px"
    className="mb-header-nav"
    data-ls-enabled={true}
    data-public={true}
    zIndex={zIndices.headerNav}
    bg={isInternalAdminDashboardView ? "primary.700" : "gray.25"}
  >
    <RouteLink
      to={rootRoute({}).$}
      minHeight="40px"
      display="flex"
      alignItems="center"
    >
      {isInternalAdminDashboardView ? <OperationsLogo /> : <Logo />}
    </RouteLink>
    <HStack spacing="0" columnGap="4px" minWidth="0" flexWrap="wrap">
      {primaryNavItems.map((navItem) => (
        <HeaderNavItem key={navItem.id} item={navItem} />
      ))}
      <AppEnvironmentBadge />
    </HStack>
    <HStack flex="1" justifyContent="flex-end" spacing="4px">
      {children}
      <Menu offset={[0, 0]} isLazy={true} lazyBehavior="unmount">
        <MenuButton
          as={IconButton}
          icon={
            <SettingsIcon
              w="20px"
              h="21px"
              color={isInternalAdminDashboardView ? "white " : "gray.500"}
            />
          }
          variant="ghost"
          size="md-icon"
        />
        <MenuList>
          {userNavigationItems.map((item) => (
            <MenuItem
              as={RouteLink}
              key={item.id}
              to={item.href}
              prefetch={true}
              _hover={{
                textDecoration: "none",
              }}
              _focusVisible={{
                boxShadow: "none",
              }}
            >
              {item.name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </HStack>
  </Flex>
);

export default HeaderNav;
